import { useContext, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import AppContext from "../context/AppContext";
import { NAVIGATION_ROUTES } from "../utils/NavigationUtils";
import { activeProducts, isEntryProductReachable } from "./guardTriage";
import { findFirstMatchingRoute } from "./redirectPriority";
import SectionUnavailable from "./SectionUnavailable";

type AppContextShape = {
  adminPermissions?: string[] | null;
};

export type GuardFallbackRedirectProps = {
  /** Explicit target from the guard's `redirectTo` prop. Honoured unless it
   *  points at the current path (which would self-redirect-loop). */
  redirectTo?: string;
};

/**
 * Redirect target for a failed route-mode guard (`FeatureFlagGuard`).
 *
 * Historically the guard's default `redirectTo` was `/kpis`, so a failed
 * gate on the KPIs route itself navigated back to `/kpis` in an infinite
 * loop. This component instead walks `REDIRECT_PRIORITY` (the same
 * permission-aware order used for the post-login landing) while excluding
 * the current pathname AND any destination whose own product gate is
 * disabled — otherwise a disabled umbrella product (e.g. `common`) turns
 * the fallback into a ping-pong between two blocked routes. When nothing
 * is reachable it renders a terminal `SectionUnavailable` message rather
 * than redirecting or going blank.
 *
 * Logging policy: only the terminal dead-end warns here (abnormal path).
 * A successful fallback redirect is normal operation and stays silent —
 * the preceding gate-failure warn in `FeatureFlagGuard` already explains
 * why the navigation happened.
 */
const GuardFallbackRedirect = ({
  redirectTo,
}: GuardFallbackRedirectProps): ReactNode => {
  const { pathname } = useLocation();
  const ctx = (useContext(AppContext) ?? {}) as AppContextShape;

  if (redirectTo && redirectTo !== pathname) {
    return <Navigate to={redirectTo} replace />;
  }

  const permissions = ctx.adminPermissions ?? null;
  const target = findFirstMatchingRoute(permissions, {
    excludeRoute: pathname,
    isEntryReachable: isEntryProductReachable,
  });

  if (target) {
    return <Navigate to={target} replace />;
  }

  // Profile not loaded / no recognised permission: preserve the historical
  // login-edge behavior and let RequireAuth on /users/home own it.
  if (!Array.isArray(permissions) || permissions.length === 0) {
    if (NAVIGATION_ROUTES.USERS === pathname) {
      return <SectionUnavailable />;
    }
    return <Navigate to={NAVIGATION_ROUTES.USERS} replace />;
  }

  // Permissions exist but every candidate destination is excluded or
  // product-blocked (e.g. the `common` umbrella is off and the admin only
  // holds Ops permissions). Terminal: surface it instead of looping.
  // `adminPermissions` is intentionally omitted from the payload — the
  // active-products list is sufficient for triage, and enumerating an
  // admin's permissions in a production console is security-adjacent.
  console.warn(
    `[GuardFallbackRedirect] no reachable fallback route from ${pathname}; ` +
      "every permitted destination is product-blocked — check the client's product catalog",
    { activeProducts: activeProducts() },
  );
  return <SectionUnavailable />;
};

export default GuardFallbackRedirect;
